import { useToast } from "@src/appV2/lib";
import { type LegacyRushFee } from "@src/appV2/redesign/utils/rushFee";
import { useModalState } from "@src/lib/ui-react";
import { isAxiosError } from "axios";
import { useCallback, useMemo } from "react";

import { useDeleteWorkplaceChargeLeadTimeDifferential } from "./api/useDeleteWorkplaceChargeLeadTimeDifferential";
import { useGetWorkplaceChargeLeadTimeDifferentials } from "./api/useGetWorkplaceChargeLeadTimeDifferentials";
import { ShiftRushFeeScheduleView } from "./ShiftRushFeeScheduleView";
import { type Differential } from "./types";

interface ShiftRushFeeScheduleContainerProps {
  workplaceId: string;
  legacyRushFee: LegacyRushFee | undefined;
}

export function ShiftRushFeeScheduleContainer(props: ShiftRushFeeScheduleContainerProps) {
  const { workplaceId, legacyRushFee } = props;

  const { showSuccessToast, showErrorToast } = useToast();
  const addModalState = useModalState();

  const { data, isLoading, isError } = useGetWorkplaceChargeLeadTimeDifferentials({ workplaceId });
  const {
    mutateAsync: deleteDifferential,
    variables: deleteVariables,
    isLoading: isDeleting,
  } = useDeleteWorkplaceChargeLeadTimeDifferential();

  const differentials = useMemo(
    () =>
      (data?.data ?? [])
        .map((differential) => ({
          id: differential.id,
          thresholdInHours: differential.attributes.thresholdInHours,
          incrementalMultiplier: differential.attributes.incrementalMultiplier,
        }))
        .sort((a, b) => a.thresholdInHours - b.thresholdInHours),
    [data]
  );

  const handleDeleteClick = useCallback(
    async (differential: Differential) => {
      try {
        await deleteDifferential({ workplaceId, differentialId: differential.id });
        showSuccessToast("Tier deleted");
      } catch (error) {
        let errorMessage = "Failed to delete tier";
        if (isAxiosError<{ message?: string }>(error) && error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        showErrorToast(errorMessage);
      }
    },
    [deleteDifferential, showErrorToast, showSuccessToast, workplaceId]
  );

  const isDeletingId = isDeleting ? deleteVariables?.differentialId : undefined;

  return (
    <ShiftRushFeeScheduleView
      addModalState={addModalState}
      differentials={differentials}
      hasError={isError}
      isDeletingId={isDeletingId}
      isLoading={isLoading}
      legacyRushFee={legacyRushFee}
      workplaceId={workplaceId}
      onAddClick={addModalState.openModal}
      onDeleteClick={handleDeleteClick}
    />
  );
}
